import HaspObject from './HaspObject';
import HaspButton from './HaspButton';
import HaspMtrxButton from './HaspMtrxButton';
import { nextTick } from 'vue';
import { FontBinding } from '../services/FontBinding.js';
import { useFontStore } from '../stores/fontStore.js';
import { contentBox, onPaddingStyle } from './properties/v2/padding.js';
// import MoveButton from '../draw/moveButton';
// import Konva from 'konva';

// options	    json array	"Text"	Json array of strings where each element is the label of a button. Use "\n" for a new line of buttons
// align	    string	    center	Text alignment: left, center, right
// toggle	    bool	    false	All buttons behave as toggle buttons or normal buttons
// one_check	bool	    false	Allow only one button to be checked (toggled) at once
// val	        int8	    0	    The number of the active button, starting at 0. In conjunction with toggle and one_check set it to -1 to deactivate all buttons

// item_buttons.bg_color is the button background color

export default class HaspButtonmatrix extends HaspObject {

    buttonList = [];
    buttPadding = 5;

    constructor(config) {
        super(config);
        this.obj='btnmatrix';
        this._fontBinding = new FontBinding((family) => {
            this._itemFontFamily = family;
            this.buttonList.forEach(element => {
                if (element instanceof HaspMtrxButton) {
                    element.textObj.fontFamily(family);
                }
            });
            this.getLayer()?.batchDraw();
        });
        //check for listOptions and load the internal structure with the config data
        if (config.options)
            this.loadOptions(config.options);
        // set the transfomer handlers.
        this.on('transform', (e) => {
            this.updateButtons();
            // this.handleButton.y(this.height() - 20);
        });
        this.on('transformend', (e) => {
            this.updateButtons();
            // this.handleButton.y(this.height() - 20);
            this.size(this.rect.size());
        });
        // Shorthand + all four per-side overrides, one subscription helper.
        onPaddingStyle(this, 'main', () => {
            this.updateButtons();
        })
        this.onStyle('item_buttons', 'text_font', () => {
            const { text_font } = this.style('item_buttons');
            this.buttonList.forEach(element => {
                if (element instanceof HaspMtrxButton) {
                    element.textObj.fontSize(text_font);
                }
            });
        });
        this.on('font', () => {
            this._applyItemFont();
        });
        this.onStyle('item_buttons', 'text_color', () => {
            const { text_color } = this.style('item_buttons');
            this.buttonList.forEach(element => {
                if (element instanceof HaspMtrxButton) {
                    element.textObj.fill(text_color);
                }
            });
        });
        this.onStyle('item_buttons', 'bg_color', () => {
            const { bg_color } = this.style('item_buttons');
            this.buttonList.forEach(element => {
                if (element instanceof HaspMtrxButton) {
                    element.rect.fill(bg_color);
                }
            });
        });
        this.onStyle('item_buttons', 'border_color', () => {
            const { border_color } = this.style('item_buttons');
            this.buttonList.forEach(element => {
                if (element instanceof HaspMtrxButton) {
                    element.rect.stroke(border_color);
                }
            });
        });
        this.onStyle('item_buttons', 'border_width', () => {
            const { border_width } = this.style('item_buttons');
            this.buttonList.forEach(element => {
                if (element instanceof HaspMtrxButton) {
                    element.rect.strokeWidth(border_width);
                }
            });
        });
        this.onStyle('item_buttons', 'radius', () => {
            const { radius } = this.style('item_buttons');
            this.buttonList.forEach(element => {
                if (element instanceof HaspMtrxButton) {
                    element.rect.cornerRadius(radius);
                }
            });
        });
        // Bug fix: was this.on('text_font_family', ...) reading main's key,
        // but text_font_family lives on item_buttons. This listener never
        // fired from a real style change before.
        this.onStyle('item_buttons', 'text_font_family', () => {
            // A loaded font resource owns fontFamily while active — FontBinding
            // itself skips the callback in that case (see HaspLabel.js).
            this._applyItemFont();
        });

        this.on('destroy', () => {
            this._fontBinding.dispose();
        });

        this._applyItemFont();
        this.drawButtons();
    }

    /**
     * The uploaded Font Resource `this.font` resolves to for canvas preview
     * — see HaspLabel.js's identically-named helper for the full semantics.
     */
    _fontResource() {
        return this.font ? useFontStore().fonts.find(f => f.file === this.font) : null;
    }

    /**
     * Resolve which font family the item buttons' text should render with,
     * mirroring HaspLabel.js's _applyTextFont(). Since drawButtons() creates
     * a fresh Konva.Text per HaspMtrxButton, this both updates already-drawn
     * buttons in place and keeps this._itemFontFamily current so drawButtons()
     * picks it up for buttons (re)created later (e.g. after a drag-reorder).
     */
    _applyItemFont() {
        const { text_font_family } = this.style('item_buttons');
        this._fontBinding.set(this._fontResource(), text_font_family || 'Roboto Condensed');
    }

    /**
     * Iterate the config options and for every \n char
     * we create a new row
     * @param {*} options 
     */
    loadOptions(options) {
        this.listOptions = [];
        var row = [];
        this.listOptions.push(row);
        options.forEach(option => {
            if (option === '\n') { //new row for every \n char
                row = [];
                this.listOptions.push(row);
            } else {
                row.push({ text: option })
            }
        });
    }

    clearButtons() {
        //only remove the buttons
        this.buttonList.forEach(element => {
            element.destroy();
        });
        this.buttonList = [];
    }

    addButton(config, rowindex, colindex) {
        const matrixButton = new HaspMtrxButton(config);
        //position is determined by drag and drop
        matrixButton.on('dragmove', (e) => { this.handleButtonDragMove(e); });
        // matrixButton.on('dragstart', (e) => { this.handleButtonDragStart(e); });
        matrixButton.on('dragend', (e) => { this.handleButtonDragEnd(e); });
        matrixButton.row_col = { row: rowindex, col: colindex };
        matrixButton.button_matrix = this;
        this.buttonList.push(matrixButton);
        return matrixButton;
    }

    setNewSize(size) {
        super.setNewSize(size);
        this.updateButtons();
    }

    updateButtons() {
        // Available content bounds after the effective per-side padding
        // (see v2/padding.js); the fixed gaps between buttons come on top.
        const box = contentBox(this.style('main'), this.rect.width(), this.rect.height());
        const totalRowPadding = this.buttPadding * (this.listOptions.length + 1);
        const bh = (box.height - totalRowPadding) / this.listOptions.length;

        this.buttonList.forEach(button => {
            const row = this.listOptions[button.row_col.row];
            const totalColPadding = this.buttPadding * (row.length + 1);
            const bw = (box.width - totalColPadding) / row.length;

            // Update button size
            button.width(bw);
            button.height(bh);

            // Optionally update position if necessary
            const x = box.x + this.buttPadding + (button.row_col.col * (bw + this.buttPadding));
            const y = box.y + this.buttPadding + (button.row_col.row * (bh + this.buttPadding));
            button.x(x);
            button.y(y);
            button.updateButton();
        });
    }

    //padding is the padding at the edges the size between buttons is fixed
    drawButtons() {
        this.clearButtons();

        const itemButtons = this.style('item_buttons');
        const buttPadding = this.buttPadding || 5;
        // Available content bounds after the effective per-side padding
        // (see v2/padding.js); the fixed gaps between buttons come on top.
        const box = contentBox(this.style('main'), this.rect?.width() || 0, this.rect?.height() || 0);

        const totalRowPadding = buttPadding * (this.listOptions.length + 1);
        let y = box.y + buttPadding;
        const bh = (box.height - totalRowPadding) / this.listOptions.length;
        this.listOptions.forEach((row, rowindex) => {
            let x = box.x + buttPadding;
            const totalColPadding = buttPadding * (row.length + 1);
            const bw = ((box.width - totalColPadding) / (row.length));
            row.forEach((btn, colindex) => {
                const config = {
                    x: x, y: y, width: bw, height: bh, obj: 'mtrx_btn', text: btn.text,
                    text_font: itemButtons.text_font, text_font_family: this._itemFontFamily ?? itemButtons.text_font_family, text_color: itemButtons.text_color, bg_color: itemButtons.bg_color,
                    border_width: itemButtons.border_width, border_color: itemButtons.border_color, radius: itemButtons.radius
                };
                const matrixButton = this.addButton(config, rowindex, colindex);
                this.add(matrixButton);
                x += (bw) + buttPadding;
            });
            y += (bh) + buttPadding;
        });
        this.idTextObj.moveToTop();
    }

    handleButtonDragMove(e) {
        const dragButton = e.target;
        dragButton.moveToTop();
        //keep button between limits
        if (dragButton.x() < -20) {
            dragButton.x(-20);
            if (this.debug) dragButton.rect.fill('#ffcccc');
        }
        if (dragButton.y() < -20) {
            dragButton.y(-20);
            if (this.debug) dragButton.rect.fill('#ccffcc');
        }
        if (dragButton.x() + dragButton.width() > this.rect.width() + 20) {
            dragButton.x(Math.round(this.rect.width() - dragButton.width() + 20));
            if (this.debug) dragButton.rect.fill('orange');
        }
        if (dragButton.y() + dragButton.height() > this.rect.height() + 20) {
            dragButton.y(Math.round(this.rect.height() - dragButton.height() + 20));
            if (this.debug) dragButton.rect.fill('green');
        }

        //button is ready to be deleted
        if (dragButton.x() <= -20 && dragButton.y() <= -20) {
            dragButton.rect.fill('red');
        } else {
            dragButton.rect.fill(this.style('item_buttons').bg_color);
        }
    }

    handleButtonDragEnd(e) {
        const dragButton = e.target;
        dragButton.moveToTop();
        //if dragButton in oupper left corner the delete it
        if (dragButton.x() <= -20 && dragButton.y() <= -20) {
            //delete drop zone
            this.removeButton(dragButton);
        } else if (dragButton.y() <= -20) {
            //move to new top row
            var objrow = [];
            objrow.push(dragButton);
            this.removeButton(dragButton)
            this.listOptions.splice(0, 0, objrow)
        } else if (dragButton.y() + dragButton.height() > this.rect.height()) {
            //move to new bottom row
            var objrow = [];
            objrow.push(dragButton);
            this.removeButton(dragButton)
            this.listOptions.push(objrow);
        } else {
            //find drop zone
            var pos = this.getStage().getPointerPosition();
            //get shapes in drop zone this takes a bit longer
            const shapes = this.getStage().getAllIntersections(pos);
            shapes.forEach(shape => {
                //look for the target
                const object = shape.getParent();
                if (object.obj === 'mtrx_btn') {
                    if (object._id !== dragButton._id) {
                        this.moveButton(dragButton, object)
                    }
                }
            });
        }
        // this.cleanupEmptyRows();
        this.drawButtons();
        this.getLayer().layerCallback("button-drag-end", this);
    }

    /**
     * move a button between rows or in a row
     * @param {*} sourceObject 
     * @param {*} targetObjec 
     * @returns 
     */
    moveButton(sourceObject, targetObjec) {
        const sourceRowCol = sourceObject.row_col;
        const targetRowCol = targetObjec.row_col;
        const srcArr = this.listOptions[sourceRowCol.row];
        const targetArr = this.listOptions[targetRowCol.row];
        //store element
        const src = srcArr[sourceRowCol.col];
        //remove element
        srcArr.splice(sourceRowCol.col, 1);
        //insert element on target position
        targetArr.splice(targetRowCol.col, 0, src);
        this.cleanupEmptyRows();
    }

    /**
     * insert button after row , col
     * @param {*} button 
     * @param {*} row 
     * @param {*} col 
     */
    insertButtonAfter(button, row, col) {
        var objrow = this.listOptions[row];
        if (!objrow) {
            objrow = [];
            this.listOptions.push(objrow);
        }
        objrow.splice(col + 1, 0, button)
    }

    /**
     * insert button befor row,col
     * @param {*} button 
     * @param {*} row 
     * @param {*} col 
     */
    insertButtonBefore(button, row, col) {
        var objrow = this.listOptions[row];
        if (!objrow) {
            objrow = [];
            this.listOptions.push(objrow);
        }
        objrow.splice(col, 0, button)
    }

    removeButton(button) {
        var butrow = this.listOptions[button.row_col.row]
        butrow.splice(button.row_col.col, 1)
        this.cleanupEmptyRows();
    }

    cleanupEmptyRows() {
        var rows = this.listOptions;
        this.listOptions = [];
        rows.forEach(row => {
            if (row.length > 0)
                this.listOptions.push(row)
        });
    }

    handleButtonTextChange(button) {
        const rc = button.row_col;
        this.listOptions[rc.row][rc.col].text = button.text;
    }

    addItem(item) {
        console.log('addItem', item);
        if (item instanceof HaspButton) {
            this.insertButtonAfter({ text: 'new' }, 0, 0)
            this.drawButtons();
        }
        return null;
    }

    setOneCheck(one_check) {
        this.one_check = one_check;
    }

    objectExport(page, objectData) {
        //use /n to identify new line
        let options = [];
        this.listOptions.forEach((row, index) => {
            row.forEach(btn => {
                options.push(btn.text);
            });
            if (index < this.listOptions.length - 1)
                options.push("\n");
        });
        objectData.options = options;
    }
}
